import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';

interface Trophy {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  trophyType: string;
}

interface TrophyAward {
  id: string;
  trophyId: string;
  userId: string;
  awardedAt: string;
  reason?: string;
  trophy: Trophy;
}

interface UserProfile {
  id: string;
  teamName: string;
  kitStyle?: string;
  kitPrimaryColour?: string;
  kitSecondaryColour?: string;
  createdAt: string;
}

const TROPHY_TYPE_NAMES = {
  custom: 'Custom Achievement',
  monthly_first: '🥇 Monthly Champion',
  monthly_second: '🥈 Monthly Runner-up', 
  monthly_third: '🥉 Monthly Third Place',
  annual_first: '🏆 Annual Champion',
  annual_second: '🥈 Annual Runner-up',
  annual_third: '🥉 Annual Third Place',
  league_first: '🏆 League Winner',
  league_second: '🥈 League Runner-up',
  league_third: '🥉 League Third Place'
};

const TrophyRoom: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [trophyAwards, setTrophyAwards] = useState<TrophyAward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isOwnProfile = currentUser && currentUser.id === userId;
  const targetUserId = userId || currentUser?.id;

  useEffect(() => {
    if (targetUserId) {
      fetchUserProfile();
      fetchTrophyAwards();
    }
  }, [targetUserId]);

  const fetchUserProfile = async () => {
    try {
      let response;
      if (userId) {
        response = await apiClient.get(`/users/profile/${targetUserId}`);
        setUserProfile(response.data);
      } else if (currentUser) {
        // Use current user data
        setUserProfile({
          id: currentUser.id,
          teamName: currentUser.teamName,
          kitStyle: currentUser.kitStyle,
          kitPrimaryColour: currentUser.kitPrimaryColour,
          kitSecondaryColour: currentUser.kitSecondaryColour,
          createdAt: currentUser.createdAt
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setError('Failed to load user profile');
    }
  };

  const fetchTrophyAwards = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let endpoint = '/trophies/user';
      if (userId) {
        endpoint = `/trophies/user/${targetUserId}`;
      }
      
      const response = await apiClient.get(endpoint);
      setTrophyAwards(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load trophies');
    } finally {
      setLoading(false);
    }
  };

  const formatAwardDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getTrophyTypeIcon = (trophyType: string): string => {
    if (trophyType.includes('first') || trophyType.includes('champion')) return '🏆';
    if (trophyType.includes('second')) return '🥈';
    if (trophyType.includes('third')) return '🥉';
    return '⭐';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highlight"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            User not found
          </h1>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">

        <Link to="/" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Home
        </Link>


      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {isOwnProfile ? 'My Trophy Room' : `${userProfile.teamName}'s Trophy Room`}
          </h1>
        </div>
      </div>


            {/* Stats Section */}
            {trophyAwards.length > 0 && (
        <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: 'var(--highlight)' }}>
                {trophyAwards.length}
              </div>
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Total Trophies
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: 'var(--highlight)' }}>
                {trophyAwards.filter(a => a.trophy.trophyType.includes('first')).length}
              </div>
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Gold Trophies
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: 'var(--highlight)' }}>
                {trophyAwards.filter(a => a.trophy.trophyType.includes('second')).length}
              </div>
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Silver Trophies
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: 'var(--highlight)' }}>
                {trophyAwards.filter(a => a.trophy.trophyType.includes('third')).length}
              </div>
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Bronze Trophies
              </div>
            </div>
          </div>
        </div>
      )}

      {trophyAwards.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 className="text-xl font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            {isOwnProfile ? 'No trophies yet' : 'No trophies earned'}
          </h3>
          <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
            {isOwnProfile 
              ? 'Start making predictions and competing in leagues to earn your first trophy!'
              : 'This user hasn\'t earned any trophies yet.'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-6">
          {trophyAwards.map((award) => (
            <div
              key={award.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-200"
            >
              {/* Trophy Image */}
              <div className="aspect-square bg-gray-200 dark:bg-gray-700 flex items-center justify-center p-2">
                {award.trophy.imageUrl ? (
                  <img
                    src={award.trophy.imageUrl}
                    alt={award.trophy.name}
                    className="w-full h-full object-contain max-w-40 max-h-40"
                  />
                ) : (
                  <div className="text-6xl">
                    {getTrophyTypeIcon(award.trophy.trophyType)}
                  </div>
                )}
              </div>

              {/* Trophy Info */}
              <div className="p-4">
                <div className="mb-2">
                  <h3 className="font-bold text-lg mb-1" style={{ color: 'var(--text-primary)' }}>
                    {award.trophy.name}
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {TROPHY_TYPE_NAMES[award.trophy.trophyType as keyof typeof TROPHY_TYPE_NAMES] || 'Achievement'}
                  </p>
                </div>

                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                  {award.trophy.description}
                </p>

                {award.reason && (
                  <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border-l-4 border-blue-400">
                    <p className="text-sm font-medium" style={{ color: 'var(--highlight)' }}>
                      {award.reason}
                    </p>
                  </div>
                )}

                <div className="flex items-center text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Earned {formatAwardDate(award.awardedAt)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}


    </div>
  );
};

export default TrophyRoom;